import { useState, useRef, useCallback, useEffect } from "react";
import { getToolAnimation } from "../config/toolAnimations";
import type { ToolPhase } from "../config/toolAnimations";

export interface ToolAnimationState {
  active: boolean;
  toolName: string | null;
  label: string;
  progress: number;
}

const INITIAL_STATE: ToolAnimationState = {
  active: false,
  toolName: null,
  label: "",
  progress: 0,
};

export function useToolAnimation() {
  const [state, setState] = useState<ToolAnimationState>(INITIAL_STATE);

  const phaseIndexRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const rafRef = useRef<number | undefined>(undefined);
  const lastEmittedRef = useRef(0);
  const activeRef = useRef(false);
  const toolNameRef = useRef<string | null>(null);

  const clearTimers = useCallback(() => {
    clearTimeout(timerRef.current);
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
  }, []);

  // Smooth progress interpolation via requestAnimationFrame
  const animateProgress = useCallback(
    (from: number, to: number, duration: number, onDone: () => void) => {
      const startTime = performance.now();

      const tick = (now: number) => {
        const elapsed = now - startTime;
        const t = Math.min(elapsed / duration, 1);
        // ease-out cubic
        const eased = 1 - Math.pow(1 - t, 3);
        const rounded = Math.round(from + (to - from) * eased);

        // Only update state when the integer value actually changes
        if (rounded !== lastEmittedRef.current) {
          lastEmittedRef.current = rounded;
          setState((prev) => ({ ...prev, progress: rounded }));
        }

        if (t < 1) {
          rafRef.current = requestAnimationFrame(tick);
        } else {
          onDone();
        }
      };

      rafRef.current = requestAnimationFrame(tick);
    },
    []
  );

  // Ref to allow recursive calls without self-referencing the useCallback
  const runPhaseRef =
    useRef<
      (phases: ToolPhase[], index: number, currentProgress: number) => void
    >(null);

  const runPhase = useCallback(
    (phases: ToolPhase[], index: number, currentProgress: number) => {
      if (index >= phases.length) return; // stall at last target

      const phase = phases[index];
      console.log(
        "[ToolAnim] runPhase index:",
        index,
        "label:",
        phase.label,
        "target:",
        phase.targetProgress
      );
      setState((prev) => ({ ...prev, label: phase.label }));

      animateProgress(
        currentProgress,
        phase.targetProgress,
        phase.duration,
        () => {
          console.log(
            "[ToolAnim] phase done, transitioning from index:",
            index,
            "→",
            index + 1
          );
          phaseIndexRef.current = index + 1;
          runPhaseRef.current?.(phases, index + 1, phase.targetProgress);
        }
      );
    },
    [animateProgress]
  );

  useEffect(() => {
    runPhaseRef.current = runPhase;
  }, [runPhase]);

  // Called when tool_calling message arrives
  const start = useCallback(
    (toolName: string) => {
      console.log(
        "[ToolAnim] start() toolName:",
        toolName,
        "| activeRef:",
        activeRef.current,
        "| toolNameRef:",
        toolNameRef.current,
        "| pendingTimer:",
        timerRef.current != null
      );

      // Case 1: same tool already animating — ignore duplicate (rapid-fire same-tool events)
      if (activeRef.current && toolNameRef.current === toolName) {
        console.log(
          "[ToolAnim] start() ignoring duplicate tool_calling for:",
          toolName
        );
        return;
      }

      clearTimers();
      const config = getToolAnimation(toolName);

      const beginNew = () => {
        console.log("[ToolAnim] beginNew() starting animation for:", toolName);
        phaseIndexRef.current = 0;
        lastEmittedRef.current = 0;
        activeRef.current = true;
        toolNameRef.current = toolName;

        setState({
          active: true,
          toolName,
          label: config.phases[0].label,
          progress: 0,
        });

        runPhaseRef.current?.(config.phases, 0, 0);
      };

      // Case 2: different tool already active — switch immediately without "Done" flash
      if (activeRef.current) {
        console.log(
          "[ToolAnim] start() switching tool immediately:",
          toolNameRef.current,
          "→",
          toolName
        );
        beginNew();
      } else {
        // Case 3: fresh start
        beginNew();
      }
    },
    [clearTimers]
  );

  // Called when stream or turn_complete arrives
  const complete = useCallback(() => {
    console.log("[ToolAnim] complete() entry | activeRef:", activeRef.current);
    clearTimers();

    if (!activeRef.current) {
      console.warn(
        "[ToolAnim] complete() called but activeRef is false — skipping"
      );
      return;
    }

    setState((prev) => {
      if (!prev.active) return prev;
      return { ...prev, progress: 100, label: "Done" };
    });

    // Hold at 100% briefly, then dismiss
    timerRef.current = setTimeout(() => {
      console.log(
        "[ToolAnim] complete() 800ms dismiss timer fired | activeRef:",
        activeRef.current
      );
      activeRef.current = false;
      toolNameRef.current = null;
      setState(INITIAL_STATE);
    }, 800);
  }, [clearTimers]);

  // Called when error arrives — immediate dismissal
  const abort = useCallback(() => {
    console.log("[ToolAnim] abort() entry | activeRef:", activeRef.current);
    clearTimers();
    activeRef.current = false;
    toolNameRef.current = null;
    setState(INITIAL_STATE);
  }, [clearTimers]);

  useEffect(() => {
    return () => clearTimers();
  }, [clearTimers]);

  return { state, start, complete, abort };
}
